import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { PROJECT_ID } from '@/lib/project';
import { formatDateTime } from '@/lib/media';
import {
  parseMessageFields,
  summarizeMessage,
  type ContactInquiry,
} from '@/lib/contacts';
import { useNoindex } from '@/hooks/useNoindex';
import ContactDetailDrawer from '@/pages/admin/contacts/components/ContactDetailDrawer';

const PAGE_SIZE = 10;

export default function AdminContacts() {
  useNoindex();
  const [rows, setRows] = useState<ContactInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ContactInquiry | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('contact_inquiries')
        .select('*')
        .eq('project_id', PROJECT_ID)
        .order('created_at', { ascending: false });
      if (queryError) throw new Error(queryError.message);
      setRows((data as ContactInquiry[]) || []);
    } catch (err) {
      setError((err as Error).message || 'データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, currentPage]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-foreground-500">
            フォームから送信されたお問い合わせの一覧です。
          </p>
          <p className="mt-0.5 text-xs text-foreground-400">全 {rows.length} 件</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 self-start rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-60"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={loading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} />
          </span>
          再読み込み
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        {loading ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3 text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-2xl" />
            <span className="text-sm">読み込み中...</span>
          </div>
        ) : error ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3 text-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-error-warning-line text-lg" />
            </span>
            <p className="text-sm text-foreground-700">{error}</p>
            <button
              type="button"
              onClick={load}
              className="rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600"
            >
              再試行
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 text-center text-foreground-500">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200">
              <i className="ri-inbox-line text-lg" />
            </span>
            <p className="text-sm">お問い合わせはまだありません。</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100 text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3 font-medium">受信日時</th>
                  <th className="px-4 py-3 font-medium">メールアドレス</th>
                  <th className="px-4 py-3 font-medium">件名</th>
                  <th className="px-4 py-3 font-medium">内容</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelected(row)}
                    className="cursor-pointer border-b border-background-100 transition-colors last:border-0 hover:bg-background-100"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-foreground-600">
                      {formatDateTime(row.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-foreground-800">
                      {row.email || '-'}
                    </td>
                    <td className="px-4 py-3 text-foreground-800">{row.subject || '-'}</td>
                    <td className="px-4 py-3 text-foreground-500">
                      <span className="line-clamp-1">{summarizeMessage(row.message)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rows.length > 0 && !loading && !error && (
        <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
          <p className="text-xs text-foreground-500">
            {currentPage} / {totalPages} ページ
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage <= 1}
              className="flex items-center gap-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-arrow-left-s-line" />
              前へ
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
              className="flex items-center gap-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              次へ
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      )}

      <ContactDetailDrawer
        inquiry={selected}
        fields={selected ? parseMessageFields(selected.message) : []}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}